/* eslint-disable max-lines */
import {
  useAlarmGroups,
  useLocationIncidents,
} from '@hakimo-ui/hakimo/data-access';
import {
  LocationIncident,
  TimePeriod,
  UnifiedAlarmGroup,
} from '@hakimo-ui/hakimo/types';
import { FilterPanel } from '@hakimo-ui/hakimo/ui-filters';
import { Page } from '@hakimo-ui/hakimo/ui-layout';
import { TableData } from '@hakimo-ui/hakimo/ui-table';
import {
  appendVersionParam,
  useOnDataVersionChange,
  withAuthz,
  withErrorBoundary,
} from '@hakimo-ui/hakimo/util';
import { Alert } from '@hakimo-ui/shared/ui-base';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  defaultFilterValues,
  filterConfigs,
  LOCATION_INCIDENTS_QUERY_INTERVAL,
} from '../constants';
import {
  locationIncidentsFiltersAtom,
  locationIncidentsPageAtom,
  locationIncidentsPageSizeAtom,
  locationIncidentsProductTypeAtom,
  locationIncidentsShownColumnsAtom,
  locationIncidentsTimePeriodAtom,
  ProductType,
} from '../state';
import LocationIncidentsTable from './LocationIncidentsTable';
import ScanIncidentsTable from './ScanIncidentsTable';
import {
  getScanSearchParams,
  getScanTableData,
  getSearchParams,
  getTableData,
  isNotDefaultFilter,
  useWithTimeFilterValue,
} from './utils';

export function LocationIncidentsList() {
  const [page, setPage] = useAtom(locationIncidentsPageAtom);
  const pageSize = useAtomValue(locationIncidentsPageSizeAtom);
  const [period, setPeriod] = useAtom(locationIncidentsTimePeriodAtom);
  const [productType, setProductType] = useAtom(
    locationIncidentsProductTypeAtom
  );
  const [shownColumns, setShownColumns] = useAtom(
    locationIncidentsShownColumnsAtom
  );
  const navigate = useNavigate();

  const [total, setTotal] = useState(0);
  const [reportFilters, setReportFilters] = useAtom(
    locationIncidentsFiltersAtom
  );
  const mergedFilters = useMemo(
    () => ({ ...defaultFilterValues, ...reportFilters }),
    [reportFilters]
  );
  const hasFiltersApplied = useMemo(
    () => isNotDefaultFilter(mergedFilters),
    [mergedFilters]
  );
  const [openFilterPanel, setOpenFilterPanel] = useState(false);
  const [allIncidents, setAllIncidents] = useState<LocationIncident[]>([]);
  const [allScanIncidents, setAllScanIncidents] = useState<UnifiedAlarmGroup[]>(
    []
  );

  // Reuse the shared v2/v3 override (persisted, driven by DataVersionToggle).
  // RG-only; v3 is served as remote-guarding. Only `v3` goes on the wire, so
  // a v2 read stays byte-identical to a pre-unification request.
  //
  // The rows and total go on a flip: the query keeps previous data, so the
  // old dataset would otherwise stay on screen while `handleRowClick` built
  // `?version=v3` links around ids from the other one.
  const dataVersion = useOnDataVersionChange(() => {
    setAllIncidents([]);
    setTotal(0);
  });

  // `page` is a jotai atom, so it cannot be reset during render like the rows
  // above -- writing an external store mid-render re-renders every subscriber.
  // Without this, flipping while on page 4 can land out of range on a smaller
  // dataset and show nothing. Skipped on mount so returning from a detail page
  // keeps the page the user left.
  const pageDataVersionRef = useRef(dataVersion);
  useEffect(() => {
    if (pageDataVersionRef.current !== dataVersion) {
      pageDataVersionRef.current = dataVersion;
      setPage(1);
    }
  }, [dataVersion, setPage]);

  // RG logic
  const alarmListFilters = useWithTimeFilterValue(reportFilters, period);
  const searchParams = getSearchParams(page, pageSize, alarmListFilters);
  const fromIndex = (page - 1) * pageSize;
  const toIndex = Math.min(page * pageSize, total) - 1;

  // Scan logic
  const customRange =
    period === TimePeriod.CUSTOM_RANGE
      ? {
          start: reportFilters.time?.customRangeStart || 0,
          end: reportFilters.time?.customRangeEnd || 0,
        }
      : undefined;
  const scanSearchParams = getScanSearchParams(
    page,
    pageSize,
    period,
    customRange,
    reportFilters
  );

  const handleRowClick = useCallback(
    (
      incident: LocationIncident,
      event: React.MouseEvent | React.KeyboardEvent
    ) => {
      // Carry the v3 signal into the detail deep link so a refresh/new-tab reads
      // the same data version (the detail route sources it from the URL).
      const url = appendVersionParam(
        `/location-incidents/incident/${incident.location_alarm_id}`,
        dataVersion
      );
      const isNewTab = event.ctrlKey || event.metaKey;

      if (isNewTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(url);
      }
    },
    [navigate, dataVersion]
  );

  const handleScanRowClick = useCallback(
    (
      incident: UnifiedAlarmGroup,
      event: React.MouseEvent | React.KeyboardEvent
    ) => {
      const url = `/location-incidents/scan/${incident.id}`;
      const isNewTab = event.ctrlKey || event.metaKey;

      if (isNewTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(url);
      }
    },
    [navigate]
  );

  const onRgData = useCallback(
    (data: { items: LocationIncident[]; total: number } | undefined) => {
      if (data) {
        setAllIncidents(data.items);
        setTotal(data.total);
      }
    },
    []
  );

  const onScanData = useCallback(
    (data: { items: UnifiedAlarmGroup[]; total: number } | undefined) => {
      if (data) {
        setAllScanIncidents(data.items);
        setTotal(data.total);
      }
    },
    []
  );

  // Derived so column toggles re-render without waiting for a refetch.
  const tableData: TableData = useMemo(
    () =>
      productType === 'rg'
        ? getTableData(allIncidents, shownColumns, handleRowClick, dataVersion)
        : getScanTableData(allScanIncidents, shownColumns, handleScanRowClick),
    [
      productType,
      allIncidents,
      allScanIncidents,
      shownColumns,
      handleRowClick,
      handleScanRowClick,
      dataVersion,
    ]
  );

  const { error: rgError, isFetching: rgIsFetching } = useLocationIncidents(
    searchParams,
    LOCATION_INCIDENTS_QUERY_INTERVAL,
    onRgData,
    productType !== 'rg',
    true,
    dataVersion
  );

  const { error: scanError, isFetching: scanIsFetching } = useAlarmGroups(
    scanSearchParams,
    LOCATION_INCIDENTS_QUERY_INTERVAL,
    onScanData,
    productType !== 'scan',
    true
  );

  const error = productType === 'rg' ? rgError : scanError;
  const isFetching = productType === 'rg' ? rgIsFetching : scanIsFetching;

  const handlePeriodChange = (
    newPeriod: TimePeriod,
    newCustomRange?: { start: number; end: number }
  ) => {
    setPeriod(newPeriod);
    if (newPeriod === TimePeriod.CUSTOM_RANGE && newCustomRange) {
      setReportFilters((prev) => ({
        ...prev,
        time: {
          period: TimePeriod.CUSTOM_RANGE,
          customRangeStart: newCustomRange.start,
          customRangeEnd: newCustomRange.end,
        },
      }));
    } else {
      setReportFilters((prev) => ({
        ...prev,
        time: {
          period: newPeriod,
          customRangeStart: 0,
          customRangeEnd: 0,
        },
      }));
    }
    setPage(1);
  };

  const handleProductTypeChange = (newProductType: ProductType) => {
    setProductType(newProductType);
    setPage(1);
    setAllIncidents([]);
    setAllScanIncidents([]);
    setTotal(0);
  };

  const handleApplyFilters = (newFilters: any) => {
    setReportFilters(newFilters);
    setPage(1);
  };

  const isDataValid = !error;
  const tableLoading = false;

  return (
    <Page title="Reports" titleFontSize="large">
      {error ? (
        <Alert type="error">
          Error getting{' '}
          {productType === 'rg' ? 'location incidents' : 'scan incidents'}
        </Alert>
      ) : (
        <div className="dark:bg-dark-bg -mx-4 flex min-h-0 flex-1 flex-col justify-start overflow-y-hidden sm:-mx-6 md:mx-0 md:rounded-lg">
          {productType === 'rg' ? (
            <LocationIncidentsTable
              allIncidents={allIncidents}
              period={period}
              productType={productType}
              fromIndex={fromIndex}
              toIndex={toIndex}
              page={page}
              pageSize={pageSize}
              total={total}
              loading={tableLoading}
              isLoadingData={isFetching}
              tableData={tableData}
              shownColumns={shownColumns}
              onChangeShownColumns={setShownColumns}
              onChangePeriod={handlePeriodChange}
              onChangeProductType={handleProductTypeChange}
              onChangePage={setPage}
              onOpenFilterPanel={() => setOpenFilterPanel(true)}
              isDataValid={isDataValid}
              hasFiltersApplied={hasFiltersApplied}
            />
          ) : (
            <ScanIncidentsTable
              allIncidents={allScanIncidents}
              period={period}
              productType={productType}
              fromIndex={fromIndex}
              toIndex={toIndex}
              page={page}
              pageSize={pageSize}
              total={total}
              loading={tableLoading}
              isLoadingData={isFetching}
              tableData={tableData}
              shownColumns={shownColumns}
              onChangeShownColumns={setShownColumns}
              onChangePeriod={handlePeriodChange}
              onChangeProductType={handleProductTypeChange}
              onChangePage={setPage}
              onOpenFilterPanel={() => setOpenFilterPanel(true)}
              isDataValid={isDataValid}
              hasFiltersApplied={hasFiltersApplied}
            />
          )}
        </div>
      )}
      <FilterPanel
        title="Reports Filters"
        open={openFilterPanel}
        filterConfigs={filterConfigs}
        initialFilterValues={mergedFilters}
        trackingEventNameApply="apply_location_alarms_filters"
        trackingEventNameReset="reset_location_alarms_filters"
        onClose={() => setOpenFilterPanel(false)}
        onApply={handleApplyFilters}
      />
    </Page>
  );
}

export default withAuthz(withErrorBoundary(LocationIncidentsList), [
  'location_incidents:view',
]);
